export type RemotePresenceCursorLabelPlacement =
  | {
      cursorHeight: number;
      placement: 'above';
    }
  | {
      placement: 'below';
    };

export const REMOTE_PRESENCE_COLORS = [
  'var(--streamlain-multi-players-purple)',
  'var(--streamlain-multi-players-magenta)',
  'var(--streamlain-multi-players-red)',
  'var(--streamlain-multi-players-orange)',
  'var(--streamlain-multi-players-green)',
  'var(--streamlain-multi-players-blue)',
  'var(--streamlain-multi-players-brown)',
  'var(--streamlain-multi-players-grey)',
] as const;

export const DEFAULT_REMOTE_PRESENCE_COLOR = REMOTE_PRESENCE_COLORS[0];
export const REMOTE_PRESENCE_CURSOR_RESPONSE_MS = 45;
export const REMOTE_PRESENCE_CURSOR_SNAP_THRESHOLD = 0.5;

export interface RemotePresenceCursorPosition {
  x: number;
  y: number;
}

export function stepRemotePresenceCursorPosition(args: {
  current: RemotePresenceCursorPosition;
  target: RemotePresenceCursorPosition;
  elapsedMs: number;
}): { next: RemotePresenceCursorPosition; moving: boolean } {
  const dx = args.target.x - args.current.x;
  const dy = args.target.y - args.current.y;
  if (
    Math.abs(dx) <= REMOTE_PRESENCE_CURSOR_SNAP_THRESHOLD &&
    Math.abs(dy) <= REMOTE_PRESENCE_CURSOR_SNAP_THRESHOLD
  ) {
    return {
      moving: false,
      next: {
        x: args.target.x,
        y: args.target.y,
      },
    };
  }

  const elapsed = Math.min(Math.max(args.elapsedMs, 0), 64);
  const lerpFactor =
    1 - Math.exp(-elapsed / REMOTE_PRESENCE_CURSOR_RESPONSE_MS);
  return {
    moving: true,
    next: {
      x: args.current.x + dx * lerpFactor,
      y: args.current.y + dy * lerpFactor,
    },
  };
}

export function getRemotePresenceColor(seed: string | number): string {
  const value = String(seed);
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return (
    REMOTE_PRESENCE_COLORS[hash % REMOTE_PRESENCE_COLORS.length] ??
    DEFAULT_REMOTE_PRESENCE_COLOR
  );
}

export function createRemotePresenceCursorLabelStyle(
  color: string,
  placement: RemotePresenceCursorLabelPlacement
): Record<string, string> {
  return {
    position: 'absolute',
    left: '-4px',
    ...(placement.placement === 'above'
      ? { bottom: `${placement.cursorHeight - 4}px` }
      : { top: 'calc(100% + 3px)' }),
    backgroundColor: color,
    color: 'white',
    maxWidth: '160px',
    padding: '0 5px',
    border: '1px solid var(--streamlain-pure-black-20)',
    boxShadow: '0px 1px 6px 0px rgba(0, 0, 0, 0.16)',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
    lineHeight: '18px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };
}

function getCurrentOrigin(): string | undefined {
  return typeof location === 'undefined' ? undefined : location.origin;
}

export function isRenderableRemotePresenceAvatarUrl(
  url: string | null | undefined,
  baseOrigin = getCurrentOrigin()
): url is string {
  const value = url?.trim();
  if (!value) {
    return false;
  }

  if (value.startsWith('blob:')) {
    return true;
  }

  if (value.startsWith('data:image/')) {
    return true;
  }

  if (!baseOrigin) {
    return false;
  }

  try {
    const parsed = new URL(value, baseOrigin);
    return (
      (parsed.protocol === 'http:' || parsed.protocol === 'https:') &&
      parsed.origin === baseOrigin
    );
  } catch {
    return false;
  }
}
